import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Users,
  Activity,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Search,
  Plus,
  Home,
  Dumbbell,
  Menu,
  X,
  Copy,
  Check,
  TrendingUp,
  Target,
  Clock,
  Timer,
  Zap,
  Star,
  Award,
  BarChart3,
  Download,
  Filter
} from 'lucide-react';
import { LineChart, AreaChart, BarChart, PieChart } from '../components/charts/CustomCharts';
import Pagination from '@mui/material/Pagination';
import '../styles/ClinicianDashboard.css';
import patientService from '../services/PatientService';
import PatientAnalyticsView from './components/PatientAnalyticsView';
import TermsAndConditions from '../components/TermsAndConditions';
import termsService from '../services/TermsService';

const ClinicianDashboard = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [newPatient, setNewPatient] = useState(null);
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState({});
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGameAnalytics, setShowGameAnalytics] = useState(false);
  const [showAssignExercises, setShowAssignExercises] = useState(false);
  const [assigningToPatient, setAssigningToPatient] = useState(null);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [reportsSelectedPatientId, setReportsSelectedPatientId] = useState(null);
  const [dashboardPage, setDashboardPage] = useState(1);
  const [dashboardFilter, setDashboardFilter] = useState('all'); // 'all', 'active', 'discharged'
  const [patientsFilter, setPatientsFilter] = useState('all'); // 'all', 'active', 'discharged' for patients tab
  const [patientsPage, setPatientsPage] = useState(1);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  // Check if terms have been accepted - will be loaded from database
  const [showTerms, setShowTerms] = useState(false);
  const [termsLoading, setTermsLoading] = useState(true);

  // Helper function to format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} week${Math.floor(diffInDays / 7) !== 1 ? 's' : ''} ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} month${Math.floor(diffInDays / 30) !== 1 ? 's' : ''} ago`;
    return `${Math.floor(diffInDays / 365)} year${Math.floor(diffInDays / 365) !== 1 ? 's' : ''} ago`;
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastInitial: '',
    roomNumber: '',
    bedNumber: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Get clinician data from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clinician = {
    name: user.name || user.email?.split('@')[0] || 'Clinician',
    initials: (user.name || user.email?.split('@')[0] || 'C').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  };

  // Available games - only upper body and core exercises (no leg games)
  const availableGames = [
    {
      id: 1,
      title: 'Apple Pick',
      type: 'Upper Body',
      description: 'Overhead reach',
      color: '#ef4444',
      icon: '/logos/apple_logo.svg'
    },
    {
      id: 2,
      title: 'Super Punch',
      type: 'Upper Body',
      description: 'Contralateral punch',
      color: '#f59e0b',
      icon: '/logos/super_punch_logo.svg'
    },
    {
      id: 3,
      title: 'Hot Potato',
      type: 'Core',
      description: 'Trunk rotation',
      color: '#ec4899',
      icon: '/logos/hot_potato_logo.svg'
    },
    {
      id: 4,
      title: 'Tomato Dodge',
      type: 'Core',
      description: 'Lateral trunk flexion',
      color: '#ef4444',
      icon: '/logos/tomato_logo.svg'
    },
    {
      id: 5,
      title: 'Ocean Row',
      type: 'Upper Body',
      description: 'Upper extremity row',
      color: '#3b82f6',
      icon: '/logos/row_logo.svg'
    },
    {
      id: 6,
      title: 'Sky Flight',
      type: 'Upper Body',
      description: 'Shoulder abduction',
      color: '#06b6d4',
      icon: '/logos/sky_flight_logo.svg'
    },
    {
      id: 7,
      title: 'Plant Care',
      type: 'Upper Body',
      description: 'Shoulder forward flexion',
      color: '#10b981',
      icon: '/logos/garden_logo.svg'
    },
    {
      id: 8,
      title: 'Berry Harvest',
      type: 'Upper Body',
      description: 'Elbow flexion',
      color: '#8b5cf6',
      icon: '/logos/berry_picking_logo.svg',
      disabled: true,
      comingSoon: true
    }
  ];

  // Check terms acceptance on mount
  useEffect(() => {
    const checkTermsAcceptance = async () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.userId || user.id;
      
      if (userId) {
        const accepted = await termsService.checkUserTermsAcceptance(userId);
        setShowTerms(!accepted);
      } else {
        // No user ID, show terms by default
        setShowTerms(true);
      }
      setTermsLoading(false);
    };

    checkTermsAcceptance();
  }, []);

  // Load patients from database on mount
  useEffect(() => {
    const loadPatients = async () => {
      try {
        // Get clinician ID from localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const clinicianId = user.userId || user.id;

        if (clinicianId) {
          // Load patients from database
          const dbPatients = await patientService.getClinicianPatients(clinicianId);
          setPatients(dbPatients);
          console.log(`Loaded ${dbPatients.length} patients from database:`, dbPatients);
        } else {
          // If no clinician ID, use mock data for testing
          console.log('Using mock data - no clinician ID found');
          const mockPatients = await patientService.getClinicianPatients('mock');
          setPatients(mockPatients);
          console.log('Mock patients:', mockPatients);
        }
      } catch (error) {
        console.error('Error loading patients:', error);
        // Fallback to mock data if database fails - same as PatientService
        const now = Date.now();
        const mockPatients = [];
        const firstNames = ['John', 'Mary', 'Robert', 'Patricia', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Jennifer'];
        const lastInitials = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K'];

        // Generate 30 mock patients to test pagination
        for (let i = 1; i <= 30; i++) {
          const isActive = i <= 25; // First 25 active, last 5 discharged
          const hoursAgo = Math.floor(Math.random() * 72) + 1;
          const daysAgo = Math.floor(Math.random() * 90) + 1;

          mockPatients.push({
            id: i,
            firstName: firstNames[i % 10],
            lastInitial: lastInitials[i % 10],
            roomNumber: `${100 + i}${i % 3 === 0 ? 'A' : i % 3 === 1 ? 'B' : ''}`,
            bedNumber: i % 4 === 0 ? '' : `${(i % 3) + 1}`,
            accessCode: `${100000 + i}`,
            status: isActive ? 'active' : 'discharged',
            assignedExercises: Math.floor(Math.random() * 8) + 3,
            completedExercises: Math.floor(Math.random() * 8),
            lastActive: new Date(now - hoursAgo * 60 * 60 * 1000).toISOString(),
            therapyFrequency: i % 3 === 0 ? 'Daily' : i % 3 === 1 ? '3x per week' : '2x per week',
            createdAt: new Date(now - daysAgo * 24 * 60 * 60 * 1000).toISOString()
          });
        }
        setPatients(mockPatients);
        console.log('Fallback mock patients loaded:', mockPatients);
      }
    };

    loadPatients();
  }, []);

  // Calculate active and discharged patients
  const getPatientStats = () => {
    const activePatients = patients.filter(p => p.status === 'active').length;
    const dischargedPatients = patients.filter(p => p.status === 'discharged').length;

    return {
      active: activePatients,
      discharged: dischargedPatients
    };
  };

  const patientStats = getPatientStats();
  const stats = [
    {
      label: 'Active Patients',
      value: patientStats.active,
      color: 'text-green-600'
    },
    {
      label: 'Discharged Patients',
      value: patientStats.discharged,
      color: 'text-gray-600'
    }
  ];

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'lastInitial') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase().slice(0, 1)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }
    
    if (!formData.lastInitial.trim()) {
      errors.lastInitial = 'Last initial is required';
    }
    
    if (!formData.roomNumber.trim()) {
      errors.roomNumber = 'Room number is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsCreatingPatient(true);
      
      try {
        // Get clinician ID from localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const clinicianId = user.userId || user.id || 'mock_clinician';
        
        // Create patient in database
        const result = await patientService.createPatient(
          formData.firstName,
          formData.lastInitial,
          formData.roomNumber,
          formData.bedNumber,
          clinicianId
        );
        
        if (result.success) {
          const patientData = {
            ...formData,
            accessCode: result.accessCode,
            status: 'active',
            createdAt: new Date().toISOString(),
            lastActive: new Date().toISOString().split('T')[0],
            assignedExercises: 0,
            completedExercises: 0,
            id: result.patientId
          };
          
          setPatients(prev => [...prev, patientData]);
          setNewPatient(patientData);
          setShowPatientForm(false);
          setShowAccessCode(true);
          
          console.log('✅ Patient created with access code:', result.accessCode);
        } else {
          console.error('Failed to create patient');
          setFormErrors({ submit: 'Failed to create patient. Please try again.' });
        }
      } catch (error) {
        console.error('Error creating patient:', error);
        setFormErrors({ submit: error.message || 'Failed to create patient. Please try again.' });
      } finally {
        setIsCreatingPatient(false);
        
        if (!formErrors.submit) {
          setFormData({
            firstName: '',
            lastInitial: '',
            roomNumber: '',
            bedNumber: ''
          });
          setFormErrors({});
        }
      }
    }
  };

  const handleCopyCode = () => {
    if (newPatient) {
      navigator.clipboard.writeText(newPatient.accessCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    console.log('Patient clicked:', patient);
    // Navigate to reports view with patient selected
    setReportsSelectedPatientId(patient.id);
    setActiveView('reports');
  };

  const handleDischargePatient = async (patient, e) => {
    if (e) e.stopPropagation(); // Prevent row click

    const patientName = `${patient.firstName || patient.firstname || 'Patient'} ${patient.lastInitial || patient.lastinitial || ''}`;

    if (!window.confirm(`Are you sure you want to discharge ${patientName}? Their access code will be deactivated.`)) {
      return;
    }

    console.log('📋 Discharging patient:', patient.id, patientName);
    setIsUpdatingStatus(prev => ({ ...prev, [patient.id]: true }));

    try {
      const result = await patientService.dischargePatient(patient.id);
      console.log('📋 Discharge result:', result);

      if (result.success) {
        // Update patient in local state
        setPatients(prev => {
          const updated = prev.map(p =>
            p.id === patient.id
              ? { ...p, status: 'discharged' }
              : p
          );
          console.log('📋 Updated patients:', updated);
          return updated;
        });
        console.log('✅ Patient discharged successfully');
      } else {
        console.error('Failed to discharge patient:', result.error);
        alert('Failed to discharge patient. Please try again.');
      }
    } catch (error) {
      console.error('Error discharging patient:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsUpdatingStatus(prev => ({ ...prev, [patient.id]: false }));
    }
  };

  const handleReactivatePatient = async (patient, e) => {
    if (e) e.stopPropagation(); // Prevent row click

    setIsUpdatingStatus(prev => ({ ...prev, [patient.id]: true }));

    try {
      const result = await patientService.reactivatePatient(patient.id);
      if (result.success) {
        // Update patient in local state
        setPatients(prev => prev.map(p =>
          p.id === patient.id
            ? { ...p, status: 'active' }
            : p
        ));
        console.log('✅ Patient reactivated successfully');
      } else {
        console.error('Failed to reactivate patient:', result.error);
        alert('Failed to reactivate patient. Please try again.');
      }
    } catch (error) {
      console.error('Error reactivating patient:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsUpdatingStatus(prev => ({ ...prev, [patient.id]: false }));
    }
  };

  const filteredPatients = patients.filter(patient => {
    if (!patient) return false;
    const query = searchQuery.toLowerCase();
    
    // Handle both database field names (firstname) and local field names (firstName)
    const firstName = (patient.firstName || patient.firstname || '').toLowerCase();
    const lastInitial = (patient.lastInitial || patient.lastinitial || '').toLowerCase();
    const roomNumber = (patient.roomNumber || patient.roomnumber || '').toLowerCase();
    
    return (
      firstName.includes(query) ||
      lastInitial.includes(query) ||
      roomNumber.includes(query)
    );
  });

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'patients', label: 'Patients', icon: Users },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const renderContent = () => {
    switch(activeView) {
      case 'dashboard':
        // Filter and sort patients for dashboard
        const filteredDashboardPatients = patients.filter(patient => {
          if (dashboardFilter === 'active') return patient.status === 'active';
          if (dashboardFilter === 'discharged') return patient.status === 'discharged';
          return true; // 'all'
        }).sort((a, b) => {
          // Sort discharged patients to bottom
          if (a.status === 'discharged' && b.status === 'active') return 1;
          if (a.status === 'active' && b.status === 'discharged') return -1;
          // Then sort by created date (newest first)
          return new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0);
        });

        // Pagination
        const patientsPerPage = 10;
        const totalPages = Math.ceil(filteredDashboardPatients.length / patientsPerPage);
        const startIndex = (dashboardPage - 1) * patientsPerPage;
        const paginatedPatients = filteredDashboardPatients.slice(startIndex, startIndex + patientsPerPage);

        return (
          <>
            {/* Overview */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Overview</h1>
              <div className="flex gap-6 mb-8">
                {stats.map((stat, index) => (
                  <Card key={index} className="flex-1">
                    <CardContent className="p-6">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        {stat.label}
                      </p>
                      <p className={`text-3xl font-bold ${stat.color || 'text-gray-900'}`}>
                        {stat.value}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Patient List Section */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle>Patient List</CardTitle>
                  <div className="flex items-center gap-4">
                    {/* Filter buttons */}
                    <div className="flex items-center gap-2">
                    <Button
                      variant={dashboardFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setDashboardFilter('all');
                        setDashboardPage(1);
                      }}
                      className="h-8"
                    >
                      All ({patients.length})
                    </Button>
                    <Button
                      variant={dashboardFilter === 'active' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setDashboardFilter('active');
                        setDashboardPage(1);
                      }}
                      className="h-8"
                    >
                      Active ({patientStats.active})
                    </Button>
                    <Button
                      variant={dashboardFilter === 'discharged' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setDashboardFilter('discharged');
                        setDashboardPage(1);
                      }}
                      className="h-8"
                    >
                      Discharged ({patientStats.discharged})
                    </Button>
                    </div>
                    <Button
                      onClick={() => setActiveView('patients')}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Users className="h-4 w-4" />
                      Go to Patients Tab
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredDashboardPatients.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Patient Name</TableHead>
                          <TableHead>Room/Bed</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Therapy Frequency</TableHead>
                          <TableHead className="text-center">Games Assigned</TableHead>
                          <TableHead>Last Active</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedPatients.map(patient => (
                          <TableRow
                            key={patient.id}
                            className={`cursor-pointer hover:bg-gray-50 ${
                              patient.status === 'discharged' ? 'opacity-75' : ''
                            }`}
                            onClick={() => handlePatientClick(patient)}
                          >
                            <TableCell>
                              <span className="font-medium">
                                {patient.firstName || patient.firstname || 'Patient'} {patient.lastInitial || patient.lastinitial || ''}
                              </span>
                            </TableCell>
                            <TableCell>
                              Room {patient.roomNumber || patient.roomnumber || 'N/A'}
                              {(patient.bedNumber || patient.bednumber) && `, Bed ${patient.bedNumber || patient.bednumber}`}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                patient.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : patient.status === 'discharged'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {patient.status === 'discharged' ? 'Discharged' : 'Active'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{patient.therapyFrequency || '15 min/day × 7 days/week'}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-medium">{patient.assignedExercises || 0}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {formatRelativeTime(patient.lastActive || patient.last_active || patient.lastactive)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center mt-6">
                        <Pagination
                          count={totalPages}
                          page={dashboardPage}
                          onChange={(e, page) => setDashboardPage(page)}
                          color="primary"
                          shape="rounded"
                          showFirstButton
                          showLastButton
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No patients found</h3>
                    <p className="text-gray-600">
                      {dashboardFilter !== 'all'
                        ? `No ${dashboardFilter} patients`
                        : 'Start by creating your first patient'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        );
      
      case 'patients':
        // Filter and sort patients for patients tab
        const filteredPatientsForTab = patients.filter(patient => {
          if (patientsFilter === 'active') return patient.status === 'active';
          if (patientsFilter === 'discharged') return patient.status === 'discharged';
          return true; // 'all'
        }).filter(patient => {
          // Also apply search filter
          if (!patient) return false;
          const query = searchQuery.toLowerCase();
          const firstName = (patient.firstName || patient.firstname || '').toLowerCase();
          const lastInitial = (patient.lastInitial || patient.lastinitial || '').toLowerCase();
          const roomNumber = (patient.roomNumber || patient.roomnumber || '').toLowerCase();
          return (
            firstName.includes(query) ||
            lastInitial.includes(query) ||
            roomNumber.includes(query)
          );
        }).sort((a, b) => {
          // Sort discharged patients to bottom
          if (a.status === 'discharged' && b.status === 'active') return 1;
          if (a.status === 'active' && b.status === 'discharged') return -1;
          // Then sort by created date (newest first)
          return new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0);
        });

        // Pagination for patients tab
        const patientsPerPageTab = 25;
        const totalPatientsPages = Math.ceil(filteredPatientsForTab.length / patientsPerPageTab);
        const patientsStartIndex = (patientsPage - 1) * patientsPerPageTab;
        const paginatedPatientsTab = filteredPatientsForTab.slice(patientsStartIndex, patientsStartIndex + patientsPerPageTab);

        return (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Patient Management</h2>
              <Button onClick={() => setShowPatientForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create New Patient
              </Button>
            </div>

            {/* Search and filters on same line */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search by name or room number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
              <Button
                variant={patientsFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setPatientsFilter('all');
                  setPatientsPage(1);
                }}
                className="h-8"
              >
                All ({patients.length})
              </Button>
              <Button
                variant={patientsFilter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setPatientsFilter('active');
                  setPatientsPage(1);
                }}
                className="h-8"
              >
                Active ({patientStats.active})
              </Button>
              <Button
                variant={patientsFilter === 'discharged' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setPatientsFilter('discharged');
                  setPatientsPage(1);
                }}
                className="h-8"
              >
                Discharged ({patientStats.discharged})
              </Button>
              </div>
            </div>

            {filteredPatientsForTab.length > 0 ? (
              <Card>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient Name</TableHead>
                        <TableHead>Room/Bed</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Therapy Frequency</TableHead>
                        <TableHead className="text-center">Assigned</TableHead>
                        <TableHead className="text-center">Completed</TableHead>
                        <TableHead>Last Active</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedPatientsTab.map(patient => (
                      <TableRow
                        key={patient.id}
                        className={`cursor-pointer hover:bg-gray-50 ${
                          patient.status === 'discharged' ? 'opacity-75' : ''
                        }`}
                        onClick={() => handlePatientClick(patient)}
                      >
                        <TableCell>
                          <span className="font-medium">
                            {patient.firstName || patient.firstname || 'Patient'} {patient.lastInitial || patient.lastinitial || ''}
                          </span>
                        </TableCell>
                        <TableCell>
                          Room {patient.roomNumber || patient.roomnumber || 'N/A'}
                          {(patient.bedNumber || patient.bednumber) && `, Bed ${patient.bedNumber || patient.bednumber}`}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            patient.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : patient.status === 'discharged'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {patient.status === 'discharged' ? 'Discharged' : 'Active'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{patient.therapyFrequency || '15 min/day × 7 days/week'}</span>
                        </TableCell>
                        <TableCell className="text-center">{patient.assignedExercises}</TableCell>
                        <TableCell className="text-center">{patient.completedExercises}</TableCell>
                        <TableCell>{formatRelativeTime(patient.lastActive || patient.last_active || patient.lastactive)}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            {patient.status === 'active' && (
                              <div className="relative group">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const code = patient.accessCode || '';
                                    navigator.clipboard.writeText(code);
                                    setCopiedCode(code);
                                    setShowCopyModal(true);
                                  }}
                                >
                                  <Copy className="h-4 w-4" />
                                  <span className="sr-only">Copy Code</span>
                                </Button>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                  Copy login code
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-800 rotate-45"></div>
                                </div>
                              </div>
                            )}
                            {patient.status === 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setAssigningToPatient(patient);

                                  // Load currently assigned exercises for this patient
                                  try {
                                    const assignments = await patientService.getPatientAssignments(patient.id);
                                    if (assignments && assignments.length > 0) {
                                      // Map exercise names to game IDs
                                      const assignedIds = assignments
                                        .map(a => {
                                          const gameName = a.exercise_name || a.exerciseName;
                                          const game = availableGames.find(g => g.title === gameName);
                                          return game ? game.id : null;
                                        })
                                        .filter(id => id !== null);

                                      // Remove duplicates
                                      const uniqueIds = [...new Set(assignedIds)];
                                      setSelectedExercises(uniqueIds);
                                    } else {
                                      setSelectedExercises([]);
                                    }
                                  } catch (error) {
                                    console.error('Error loading assignments:', error);
                                    setSelectedExercises([]);
                                  }

                                  setShowAssignExercises(true);
                                }}
                              >
                                Assign Exercises
                              </Button>
                            )}
                            {patient.status === 'active' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handleDischargePatient(patient, e);
                                }}
                                disabled={isUpdatingStatus[patient.id]}
                              >
                                {isUpdatingStatus[patient.id] ? 'Processing...' : 'Discharge'}
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handleReactivatePatient(patient, e);
                                }}
                                disabled={isUpdatingStatus[patient.id]}
                              >
                                {isUpdatingStatus[patient.id] ? 'Processing...' : 'Reactivate'}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>

                {/* Pagination */}
                {totalPatientsPages > 1 && (
                  <div className="flex justify-center mt-6">
                    <Pagination
                      count={totalPatientsPages}
                      page={patientsPage}
                      onChange={(e, page) => setPatientsPage(page)}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                    />
                  </div>
                )}
              </Card>
            ) : (
              <Card className="p-12">
                <div className="text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No patients found</h3>
                  <p className="text-gray-600 mb-4">
                    {searchQuery 
                      ? 'Try adjusting your search terms'
                      : 'Create your first patient to get started'}
                  </p>
                  {!searchQuery && (
                    <Button onClick={() => setShowPatientForm(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create First Patient
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </>
        );

      case 'reports':
        return (
          <>
            {/* Patient Selector */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Reports & Analytics</h2>
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">Select Patient:</label>
                <select
                  className="px-4 py-2 pr-10 border rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6d4f79] appearance-none bg-no-repeat bg-right"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                  value={reportsSelectedPatientId || ''}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    console.log('📊 Selected patient ID:', selectedValue, 'Type:', typeof selectedValue);
                    setReportsSelectedPatientId(selectedValue || null);
                  }}
                >
                  <option value="">-- Select a Patient --</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.firstName || patient.firstname} {patient.lastInitial || patient.lastinitial} -
                      Room {patient.roomNumber || patient.roomnumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Patient Analytics View */}
            <PatientAnalyticsView
              selectedPatientId={reportsSelectedPatientId}
              patients={patients}
            />
          </>
        );

      case 'reports_old':
        // MoveMend brand colors (keeping old code for reference)
        const brandColors = {
          primary: '#6d4f79',
          secondary: '#8b6897',
          accent: '#a584b3',
          light: '#c3a9ce',
          success: '#10b981',
          warning: '#f59e0b',
          danger: '#ef4444'
        };

        // Mock data for reports with proper ROM measurements
        const reportData = {
          hasData: patients.length > 0,
          dateRange: 'Last 30 Days',
          overallMetrics: {
            totalSessions: 342,
            avgFormScore: 87,
            avgShoulderROM: 165, // Shoulder flexion normal range: 150-180°
            avgElbowROM: 145,    // Elbow flexion normal range: 130-150°
            avgHipROM: 110,      // Hip flexion normal range: 100-120°
            completionRate: 89,
            totalPatients: patients.length,
            activePatients: patients.filter(p => p.status === 'active').length
          },
          // ROM data for different joints/exercises
          romTrends: [
            { week: 'W1', shoulder: 145, elbow: 130, hip: 95, knee: 120 },
            { week: 'W2', shoulder: 152, elbow: 135, hip: 100, knee: 125 },
            { week: 'W3', shoulder: 158, elbow: 140, hip: 105, knee: 128 },
            { week: 'W4', shoulder: 165, elbow: 145, hip: 110, knee: 130 }
          ],
          formScoreTrends: [
            { day: 'Mon', score: 82, sessions: 48 },
            { day: 'Tue', score: 85, sessions: 52 },
            { day: 'Wed', score: 87, sessions: 49 },
            { day: 'Thu', score: 86, sessions: 45 },
            { day: 'Fri', score: 89, sessions: 51 },
            { day: 'Sat', score: 88, sessions: 47 },
            { day: 'Sun', score: 84, sessions: 50 }
          ],
          exerciseDistribution: [
            { name: 'Upper Body', value: 45, color: brandColors.primary },
            { name: 'Lower Body', value: 25, color: brandColors.secondary },
            { name: 'Core', value: 20, color: brandColors.accent },
            { name: 'Balance', value: 10, color: brandColors.light }
          ],
          patientProgress: [
            { name: 'John D', formScore: 92, shoulderROM: 170, improvement: 15 },
            { name: 'Mary S', formScore: 88, shoulderROM: 162, improvement: 12 },
            { name: 'Punit V', formScore: 85, shoulderROM: 158, improvement: 18 },
            { name: 'Test P', formScore: 79, shoulderROM: 145, improvement: 8 }
          ]
        };

        return (
          <>
            {/* Header with Date Filter */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Clinical Reports</h2>
              <div className="flex items-center gap-4">
                <select className="px-4 py-2 border rounded-lg bg-white text-sm">
                  <option>Last 30 Days</option>
                  <option>Last 7 Days</option>
                  <option>Last 3 Months</option>
                  <option>Custom Range</option>
                </select>
                <Button className="bg-[#6d4f79] hover:bg-[#5a3f66] text-white">
                  <Download className="mr-2 h-4 w-4" />
                  Export PDF
                </Button>
              </div>
            </div>

            {reportData.hasData ? (
              <>
                {/* Key Metrics Overview */}
                <div className="grid grid-cols-6 gap-4 mb-6">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4" style={{ color: brandColors.primary }} />
                        <span className="text-xs text-gray-600">Total Patients</span>
                      </div>
                      <p className="text-2xl font-bold">{reportData.overallMetrics.totalPatients}</p>
                      <p className="text-xs text-gray-500">{reportData.overallMetrics.activePatients} active</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="h-4 w-4" style={{ color: brandColors.secondary }} />
                        <span className="text-xs text-gray-600">Total Sessions</span>
                      </div>
                      <p className="text-2xl font-bold">{reportData.overallMetrics.totalSessions}</p>
                      <p className="text-xs" style={{ color: brandColors.success }}>↑ 12% from last period</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4" style={{ color: brandColors.success }} />
                        <span className="text-xs text-gray-600">Avg Form Score</span>
                      </div>
                      <p className="text-2xl font-bold">{reportData.overallMetrics.avgFormScore}%</p>
                      <p className="text-xs" style={{ color: brandColors.success }}>↑ 5% improvement</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4" style={{ color: brandColors.accent }} />
                        <span className="text-xs text-gray-600">Shoulder ROM</span>
                      </div>
                      <p className="text-2xl font-bold">{reportData.overallMetrics.avgShoulderROM}°</p>
                      <p className="text-xs text-gray-500">Normal: 150-180°</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="h-4 w-4" style={{ color: brandColors.primary }} />
                        <span className="text-xs text-gray-600">Completion Rate</span>
                      </div>
                      <p className="text-2xl font-bold">{reportData.overallMetrics.completionRate}%</p>
                      <p className="text-xs text-gray-500">Of assigned exercises</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4" style={{ color: brandColors.warning }} />
                        <span className="text-xs text-gray-600">Avg Duration</span>
                      </div>
                      <p className="text-2xl font-bold">12m</p>
                      <p className="text-xs text-gray-500">Per session</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Progress Charts */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* ROM Trends by Joint */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Range of Motion Progress (Degrees)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ width: '100%', overflowX: 'auto' }}>
                        <LineChart data={reportData.romTrends} width={450} height={250} />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Form Score Trend */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Daily Form Score Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ width: '100%', overflowX: 'auto' }}>
                        <AreaChart data={reportData.formScoreTrends} width={450} height={250} />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Additional Charts Row */}
                <div className="grid grid-cols-3 gap-6 mb-6">
                  {/* Exercise Distribution Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Exercise Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <PieChart data={reportData.exerciseDistribution} width={280} height={200} />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Patient Progress Bar Chart */}
                  <Card className="col-span-2">
                    <CardHeader>
                      <CardTitle>Individual Patient Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ width: '100%', overflowX: 'auto' }}>
                        <BarChart data={reportData.patientProgress} width={550} height={200} />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Patient Performance Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Individual Patient Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Patient</th>
                            <th className="text-center py-3 px-4">Sessions</th>
                            <th className="text-center py-3 px-4">Avg Form Score</th>
                            <th className="text-center py-3 px-4">Avg ROM</th>
                            <th className="text-center py-3 px-4">Completion</th>
                            <th className="text-center py-3 px-4">Progress</th>
                            <th className="text-center py-3 px-4">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {patients.slice(0, 5).map((patient, index) => (
                            <tr key={patient.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-8 w-8 bg-[#6d4f79]">
                                    <AvatarFallback className="bg-[#6d4f79] text-white text-xs">
                                      {(patient.firstName || patient.firstname || 'P')[0]}
                                      {patient.lastInitial || patient.lastinitial || ''}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium">
                                      {patient.firstName || patient.firstname} {patient.lastInitial || patient.lastinitial}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Room {patient.roomNumber || patient.roomnumber}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center py-3 px-4">{12 + index * 3}</td>
                              <td className="text-center py-3 px-4">
                                <span className={`font-semibold ${
                                  85 + index * 2 >= 90 ? 'text-green-600' :
                                  85 + index * 2 >= 80 ? 'text-amber-600' : 'text-red-600'
                                }`}>{85 + index * 2}%</span>
                              </td>
                              <td className="text-center py-3 px-4">{135 + index * 5}°</td>
                              <td className="text-center py-3 px-4">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-16 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="h-2 rounded-full bg-[#6d4f79]"
                                      style={{ width: `${patient.completedExercises / patient.assignedExercises * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-xs">
                                    {Math.round(patient.completedExercises / patient.assignedExercises * 100)}%
                                  </span>
                                </div>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-green-600 text-xs font-medium">↑ {5 + index}%</span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  patient.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {patient.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Blank State */
              <Card className="p-12">
                <div className="text-center max-w-md mx-auto">
                  <BarChart3 className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Report Data Available</h3>
                  <p className="text-gray-600 mb-6">
                    Reports will appear here once your patients start completing exercise sessions.
                    Get started by creating patients and assigning them exercises.
                  </p>
                  <div className="space-y-3">
                    <Button
                      onClick={() => setActiveView('patients')}
                      className="w-full bg-[#6d4f79] hover:bg-[#5a3f66] text-white"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create Your First Patient
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setActiveView('exercises')}
                      className="w-full"
                    >
                      View Exercise Library
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </>
        );
      
      case 'settings':
        return (
          <Card className="p-12">
            <div className="text-center">
              <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Settings</h3>
              <p className="text-gray-600">
                Configure your dashboard preferences and account settings
              </p>
            </div>
          </Card>
        );


      default:
        return null;
    }
  };

  return (
    <div className="clinician-dashboard">
      {/* Top Navigation Bar */}
      <header className="top-navbar">
        <div className="navbar-container">
          {/* Logo */}
          <div className="navbar-logo">
            <img src="/auth/logo.svg" alt="MoveMend" className="logo-img" />
          </div>

          {/* Navigation Items */}
          <nav className="navbar-nav">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`nav-item-horizontal ${activeView === item.id ? 'nav-item-active' : ''}`}
                  onClick={() => setActiveView(item.id)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Profile and Logout */}
          <div className="navbar-actions">
            <div className="header-profile">
              <Avatar>
                <AvatarFallback className="bg-[#6d4f79] text-white">
                  {clinician.initials}
                </AvatarFallback>
              </Avatar>
              <span className="header-name">{clinician.name.includes('@') ? clinician.name.split('@')[0] : clinician.name}</span>
            </div>
            <button className="nav-item-horizontal" onClick={() => {
              localStorage.removeItem('user');
              window.location.reload();
            }}>
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Navigation (dropdown) */}
      {sidebarOpen && (
        <div className="mobile-nav-dropdown">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={`mobile-nav-item ${activeView === item.id ? 'nav-item-active' : ''}`}
                onClick={() => {
                  setActiveView(item.id);
                  setSidebarOpen(false);
                }}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button className="mobile-nav-item" onClick={() => {
            localStorage.removeItem('user');
            window.location.reload();
          }}>
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content-horizontal">
        <div className="content">
          {renderContent()}
        </div>
      </main>

      {/* Patient Form Modal */}
      <Dialog open={showPatientForm} onOpenChange={setShowPatientForm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Patient</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePatient} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleFormChange}
                placeholder="Enter first name"
                className={formErrors.firstName ? 'border-red-500' : ''}
                disabled={isCreatingPatient}
              />
              {formErrors.firstName && (
                <p className="text-sm text-red-500">{formErrors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastInitial">
                Last Initial <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastInitial"
                name="lastInitial"
                value={formData.lastInitial}
                onChange={handleFormChange}
                placeholder="Enter last initial"
                maxLength="1"
                className={formErrors.lastInitial ? 'border-red-500' : ''}
                disabled={isCreatingPatient}
              />
              {formErrors.lastInitial && (
                <p className="text-sm text-red-500">{formErrors.lastInitial}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="roomNumber">
                Room Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="roomNumber"
                name="roomNumber"
                value={formData.roomNumber}
                onChange={handleFormChange}
                placeholder="Enter room number"
                className={formErrors.roomNumber ? 'border-red-500' : ''}
                disabled={isCreatingPatient}
              />
              {formErrors.roomNumber && (
                <p className="text-sm text-red-500">{formErrors.roomNumber}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bedNumber">
                Bed Number <span className="text-gray-400">(Optional)</span>
              </Label>
              <Input
                id="bedNumber"
                name="bedNumber"
                value={formData.bedNumber}
                onChange={handleFormChange}
                placeholder="Enter bed number"
                disabled={isCreatingPatient}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowPatientForm(false);
                  setFormData({ firstName: '', lastInitial: '', roomNumber: '', bedNumber: '' });
                  setFormErrors({});
                }}
                disabled={isCreatingPatient}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreatingPatient}
              >
                {isCreatingPatient ? 'Creating...' : 'Create Patient'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Access Code Display Modal */}
      <Dialog open={showAccessCode} onOpenChange={() => {
        setShowAccessCode(false);
        setNewPatient(null);
        setCopied(false);
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl text-green-600">
              Patient Created Successfully!
            </DialogTitle>
          </DialogHeader>
          
          {newPatient && (
            <div className="py-6">
              <div className="bg-gray-50 rounded-lg p-6 text-center mb-6">
                <p className="text-sm text-gray-600 mb-3">Access Code</p>
                <div className="text-4xl font-bold text-[#6d4f79] tracking-wider font-mono mb-4">
                  {newPatient.accessCode}
                </div>
                <Button
                  onClick={handleCopyCode}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy Code
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-white border rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-sm text-gray-700 mb-3">Patient Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{newPatient.firstName || newPatient.firstname} {newPatient.lastInitial || newPatient.lastinitial}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Room:</span>
                    <span className="font-medium">{newPatient.roomNumber || newPatient.roomnumber}</span>
                  </div>
                  {(newPatient.bedNumber || newPatient.bednumber) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Bed:</span>
                      <span className="font-medium">{newPatient.bedNumber || newPatient.bednumber}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-sm text-yellow-900 mb-2">
                  Instructions for Patient
                </h3>
                <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
                  <li>Share this access code with the patient</li>
                  <li>Patient should visit the MoveMend platform</li>
                  <li>Enter the access code to log in</li>
                  <li>Begin assigned exercises immediately</li>
                </ol>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              onClick={() => {
                setShowAccessCode(false);
                setNewPatient(null);
                setCopied(false);
              }}
              className="w-full"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Exercises Modal */}
      <Dialog open={showAssignExercises} onOpenChange={setShowAssignExercises}>
        <DialogContent className="max-w-[90vw] sm:max-w-[420px] max-h-[90vh] overflow-hidden flex flex-col bg-white">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-xl font-semibold">
              Assign Exercises to {assigningToPatient?.firstName || assigningToPatient?.firstname} {assigningToPatient?.lastInitial || assigningToPatient?.lastinitial}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 overflow-y-auto flex-1 py-4 px-2" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {/* Exercise Selection */}
            <div className="space-y-3">
              <Label className="text-lg font-semibold">Select Exercises (8 Upper Body & Core)</Label>
              <div className="grid grid-cols-2 gap-2">
                {availableGames.map((game) => {
                  const isSelected = !game.disabled && selectedExercises.includes(game.id);
                  const isDisabled = game.disabled;

                  return (
                    <button
                      key={game.id}
                      type="button"
                      disabled={isDisabled}
                      className={`
                        relative p-3 rounded-lg border-2 transition-all
                        flex flex-col items-center justify-center text-center
                        min-h-[130px] select-none
                        ${isDisabled
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : isSelected
                            ? 'border-[#6d4f79] bg-purple-50 shadow-sm cursor-pointer'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                        }
                      `}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isSelected) {
                          setSelectedExercises(prev => prev.filter(id => id !== game.id));
                        } else {
                          setSelectedExercises(prev => [...prev, game.id]);
                        }
                      }}
                    >
                      {/* Coming Soon badge */}
                      {game.comingSoon && (
                        <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-gray-500 text-white text-[10px] font-medium rounded">
                          Coming Soon
                        </div>
                      )}

                      {/* Selection checkbox - hidden for disabled games */}
                      {!isDisabled && (
                        <div className={`
                          absolute top-2 right-2 w-5 h-5 rounded border-2
                          flex items-center justify-center transition-colors shadow-sm
                          ${isSelected
                            ? 'bg-[#6d4f79] border-[#6d4f79]'
                            : 'border-gray-600 bg-white outline outline-1 outline-gray-300'
                          }
                        `}>
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                      )}

                      {/* Game icon */}
                      <img
                        src={game.icon}
                        alt={game.title}
                        className={`w-12 h-12 object-contain mb-2 ${isDisabled ? 'grayscale' : ''}`}
                      />

                      {/* Game title */}
                      <p className={`font-medium text-sm leading-tight ${isDisabled ? 'text-gray-400' : ''}`}>
                        {game.title}
                      </p>

                      {/* Game type */}
                      <p className={`text-xs mt-1 ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                        {game.type}
                      </p>

                      {/* Game description */}
                      <p className={`text-xs mt-0.5 italic ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                        {game.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Therapy Frequency Configuration */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">
                Therapy Schedule
              </Label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label htmlFor="minutes-per-day" className="text-sm text-gray-600 mb-1">
                    Minutes per day
                  </Label>
                  <Input
                    id="minutes-per-day"
                    type="number"
                    min="5"
                    max="60"
                    step="5"
                    defaultValue="15"
                    className="w-full"
                    placeholder="Minutes"
                    onChange={(e) => {
                      const mins = e.target.value || '15';
                      const days = document.getElementById('days-per-week')?.value || '7';
                      const preview = document.getElementById('frequency-preview');
                      if (preview) {
                        preview.textContent = `${mins} min/day × ${days} days/week`;
                        preview.style.color = '#6d4f79';
                      }
                    }}
                  />
                </div>
                <span className="text-lg text-gray-500 mt-6">×</span>
                <div className="flex-1">
                  <Label htmlFor="days-per-week" className="text-sm text-gray-600 mb-1">
                    Days per week
                  </Label>
                  <Input
                    id="days-per-week"
                    type="number"
                    min="1"
                    max="7"
                    defaultValue="7"
                    className="w-full"
                    placeholder="Days"
                    onChange={(e) => {
                      const days = e.target.value || '7';
                      const mins = document.getElementById('minutes-per-day')?.value || '15';
                      const preview = document.getElementById('frequency-preview');
                      if (preview) {
                        preview.textContent = `${mins} min/day × ${days} days/week`;
                        preview.style.color = '#6d4f79';
                      }
                    }}
                  />
                </div>
              </div>
              <>
                <style>
                  {`
                    #frequency-preview-container {
                      color: #6d4f79 !important;
                    }
                    #frequency-preview-container * {
                      color: #6d4f79 !important;
                    }
                    #frequency-preview {
                      color: #6d4f79 !important;
                    }
                  `}
                </style>
                <p id="frequency-preview-container" className="text-3xl font-bold" style={{ color: '#6d4f79' }}>
                  Schedule: <span id="frequency-preview" className="font-bold" style={{ color: '#6d4f79' }}>15 min/day × 7 days/week</span>
                </p>
              </>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-base font-semibold">
                Special Instructions (Optional)
              </Label>
              <textarea
                id="notes"
                rows="3"
                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6d4f79] focus:border-transparent resize-none bg-white"
                placeholder="Add any special instructions or notes for the patient..."
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2 border-t pt-4 mt-4 flex-row justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAssignExercises(false);
                setAssigningToPatient(null);
                setSelectedExercises([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (selectedExercises.length === 0) {
                  alert('Please select at least one exercise');
                  return;
                }

                // Get minutes per day and days per week from form
                const minutesPerDay = document.getElementById('minutes-per-day')?.value || '15';
                const daysPerWeek = document.getElementById('days-per-week')?.value || '7';
                const notes = document.getElementById('notes')?.value || '';

                // Format frequency string
                const frequencyString = `${minutesPerDay} min/day × ${daysPerWeek} days/week`;

                // Map selected game IDs to exercise objects
                const exercises = selectedExercises.map(gameId => {
                  const game = availableGames.find(g => g.id === gameId);
                  return {
                    id: gameId,
                    name: game.title,
                    type: game.type,
                    frequency: frequencyString,
                    sessionDuration: parseInt(minutesPerDay),
                    notes: notes
                  };
                });

                try {
                  // Get clinician ID from localStorage
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  const clinicianId = user.userId || user.id;

                  // Save to database
                  const result = await patientService.createExerciseAssignments(
                    assigningToPatient.id,
                    clinicianId,
                    exercises
                  );

                  if (result.success) {
                    alert(`Successfully assigned ${selectedExercises.length} exercise(s) to ${assigningToPatient.firstName || assigningToPatient.firstname}!`);

                    // Update patient's therapy frequency
                    await patientService.updatePatientTherapyFrequency(assigningToPatient.id, frequencyString);

                    // Update patient's assigned exercises count and frequency to match the new selection
                    setPatients(prev => prev.map(p =>
                      p.id === assigningToPatient.id
                        ? { ...p, assignedExercises: selectedExercises.length, therapyFrequency: frequencyString }
                        : p
                    ));

                    // Update the count in the database
                    await patientService.updatePatientExerciseCount(assigningToPatient.id, selectedExercises.length);
                  } else {
                    alert('Failed to save exercise assignments. Please try again.');
                  }
                } catch (error) {
                  console.error('Error saving assignments:', error);
                  alert('Error saving exercise assignments. Please try again.');
                }

                setShowAssignExercises(false);
                setAssigningToPatient(null);
                setSelectedExercises([]);
              }}
              className="bg-[#6d4f79] hover:bg-[#5a3f66] text-white"
            >
              Assign Exercises
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Code Success Modal */}
      <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">
              <div className="flex items-center justify-center mb-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
              </div>
              Login Code Copied!
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                The following access code has been copied to your clipboard:
              </p>
              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <p className="text-2xl font-bold text-[#6d4f79] font-mono tracking-wider">
                  {copiedCode}
                </p>
              </div>
              <p className="text-xs text-gray-500">
                Share this code with the patient to allow them to log in
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => {
                setShowCopyModal(false);
                setCopiedCode('');
              }}
              className="w-full"
            >
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Game Analytics Modal */}
      <Dialog open={showGameAnalytics} onOpenChange={setShowGameAnalytics}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-3">
              {selectedGame && (
                <>
                  <img
                    src={selectedGame.icon}
                    alt={selectedGame.title}
                    className="w-10 h-10 object-contain"
                  />
                  <span>{selectedGame.title} Analytics</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {selectedPatient ? (
              <>
                {/* Get analytics data for selected patient and game */}
                {(() => {
                  const gameAnalytics = {
                    'Apple Pick': { sessions: 12, accuracy: 88, completion: 92, trend: 'up', improvement: 15, duration: 145, reps: 240 },
                    'Super Punch': { sessions: 8, accuracy: 85, completion: 88, trend: 'up', improvement: 12, duration: 96, reps: 160 },
                    'Hot Potato': { sessions: 5, accuracy: 76, completion: 80, trend: 'stable', improvement: 5, duration: 60, reps: 100 },
                    'Tomato Dodge': { sessions: 7, accuracy: 84, completion: 86, trend: 'up', improvement: 11, duration: 84, reps: 140 },
                    'Ocean Row': { sessions: 6, accuracy: 79, completion: 83, trend: 'stable', improvement: 8, duration: 72, reps: 120 },
                    'Sky Flight': { sessions: 5, accuracy: 82, completion: 80, trend: 'up', improvement: 10, duration: 60, reps: 100 },
                    'Plant Care': { sessions: 10, accuracy: 91, completion: 95, trend: 'up', improvement: 18, duration: 120, reps: 200 },
                    'Berry Harvest': { sessions: 0, accuracy: 0, completion: 0, trend: 'none', improvement: 0, duration: 0, reps: 0 },
                    'Power Kick': { sessions: 0, accuracy: 0, completion: 0, trend: 'none', improvement: 0, duration: 0, reps: 0 },
                    'Ice Skate': { sessions: 0, accuracy: 0, completion: 0, trend: 'none', improvement: 0, duration: 0, reps: 0 }
                  };
                  const analytics = gameAnalytics[selectedGame?.title] || { sessions: 0, accuracy: 0, completion: 0, trend: 'none', improvement: 0, duration: 0, reps: 0 };

                  return (
                    <>
                      {/* Patient Info Header */}
                      <div className="bg-[#6d4f79]/5 border border-[#6d4f79]/20 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12 bg-[#6d4f79]">
                            <AvatarFallback className="bg-[#6d4f79] text-white">
                              {(selectedPatient.firstName || selectedPatient.firstname || 'P')[0]}
                              {selectedPatient.lastInitial || selectedPatient.lastinitial || ''}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {selectedPatient.firstName || selectedPatient.firstname} {selectedPatient.lastInitial || selectedPatient.lastinitial}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Room {selectedPatient.roomNumber || selectedPatient.roomnumber}
                              {(selectedPatient.bedNumber || selectedPatient.bednumber) && `, Bed ${selectedPatient.bedNumber || selectedPatient.bednumber}`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Performance Overview */}
                      <div className="grid grid-cols-4 gap-4">
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Activity className="h-4 w-4 text-[#6d4f79]" />
                              <span className="text-sm text-gray-600">Sessions</span>
                            </div>
                            <p className="text-2xl font-bold">{analytics.sessions}</p>
                            <p className="text-xs text-gray-500">Total played</p>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-gray-600">Accuracy</span>
                            </div>
                            <p className="text-2xl font-bold">{analytics.accuracy}%</p>
                            <p className="text-xs text-green-600">
                              {analytics.trend === 'up' ? `↑ ${Math.floor(analytics.improvement / 3)}% this week` :
                               analytics.trend === 'down' ? `↓ ${Math.floor(analytics.improvement / 3)}% this week` :
                               'Stable'}
                            </p>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="h-4 w-4 text-blue-600" />
                              <span className="text-sm text-gray-600">Avg Duration</span>
                            </div>
                            <p className="text-2xl font-bold">{analytics.sessions > 0 ? Math.floor(analytics.duration / analytics.sessions) : 0}m</p>
                            <p className="text-xs text-gray-500">Per session</p>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <TrendingUp className="h-4 w-4 text-amber-600" />
                              <span className="text-sm text-gray-600">Reps Total</span>
                            </div>
                            <p className="text-2xl font-bold">{analytics.reps}</p>
                            <p className="text-xs text-gray-500">{analytics.sessions > 0 ? Math.floor(analytics.reps / analytics.sessions) : 0} per session</p>
                          </CardContent>
                        </Card>
                      </div>

                {/* Weekly Progress Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Weekly Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Chart */}
                      <div className="h-48 flex items-end justify-between gap-2">
                        {[75, 78, 82, 85, 87, 90, 88].map((value, index) => (
                          <div key={index} className="flex-1 flex flex-col items-center gap-2">
                            <span className="text-xs text-gray-600">{value}%</span>
                            <div className="w-full bg-gray-200 rounded-t relative flex-1">
                              <div
                                className="absolute bottom-0 w-full rounded-t transition-all duration-300"
                                style={{
                                  height: `${value}%`,
                                  backgroundColor: value >= 85 ? '#10b981' : value >= 70 ? '#f59e0b' : '#ef4444'
                                }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index]}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Metrics Summary */}
                      <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                        <div>
                          <p className="text-sm text-gray-600">Best Score</p>
                          <p className="text-lg font-semibold text-green-600">95%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Completion Rate</p>
                          <p className="text-lg font-semibold">92%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Improvement</p>
                          <p className="text-lg font-semibold text-green-600">+15%</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Session History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Sessions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { date: '2024-01-09', time: '10:30 AM', duration: '12m', reps: 20, accuracy: 88, completion: 100 },
                        { date: '2024-01-08', time: '2:15 PM', duration: '11m', reps: 20, accuracy: 85, completion: 100 },
                        { date: '2024-01-07', time: '9:45 AM', duration: '13m', reps: 18, accuracy: 82, completion: 90 },
                        { date: '2024-01-06', time: '3:00 PM', duration: '10m', reps: 20, accuracy: 90, completion: 100 },
                        { date: '2024-01-05', time: '11:20 AM', duration: '12m', reps: 19, accuracy: 87, completion: 95 }
                      ].map((session, index) => (
                        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="font-medium">{session.date}</p>
                              <p className="text-sm text-gray-600">{session.time}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <p className="text-sm text-gray-600">Duration</p>
                              <p className="font-medium">{session.duration}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-600">Reps</p>
                              <p className="font-medium">{session.reps}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-600">Accuracy</p>
                              <p className="font-medium" style={{
                                color: session.accuracy >= 85 ? '#10b981' : session.accuracy >= 70 ? '#f59e0b' : '#ef4444'
                              }}>{session.accuracy}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-600">Complete</p>
                              <p className="font-medium">{session.completion}%</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* AI Insights */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">AI Insights & Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Strengths</h4>
                        <ul className="space-y-1 text-sm text-green-800">
                          <li>• Consistent improvement in accuracy over the past week</li>
                          <li>• Excellent completion rate (92%)</li>
                          <li>• Regular session frequency</li>
                        </ul>
                      </div>

                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-semibold text-amber-900 mb-2">Areas for Improvement</h4>
                        <ul className="space-y-1 text-sm text-amber-800">
                          <li>• Consider increasing session duration gradually</li>
                          <li>• Focus on maintaining form during final repetitions</li>
                          <li>• Aim for 95% accuracy consistently</li>
                        </ul>
                      </div>

                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">Recommendations</h4>
                        <ul className="space-y-1 text-sm text-blue-800">
                          <li>• Ready to progress to next difficulty level</li>
                          <li>• Consider pairing with complementary exercises</li>
                          <li>• Schedule sessions at consistent times for better adherence</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                    </>
                  );
                })()}
              </>
            ) : (
              /* General Game Info when no patient selected */
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <img
                        src={selectedGame?.icon}
                        alt={selectedGame?.title}
                        className="w-32 h-32 object-contain mx-auto mb-4"
                      />
                      <h3 className="text-xl font-semibold mb-2">{selectedGame?.title}</h3>
                      <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                        selectedGame?.type === 'Upper Body' ? 'bg-blue-100 text-blue-800' :
                        selectedGame?.type === 'Core' ? 'bg-purple-100 text-purple-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {selectedGame?.type}
                      </span>
                    </div>

                    <p className="text-gray-600 mb-6">{selectedGame?.description}</p>

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Exercise Benefits</h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                          <li>• Improves range of motion and flexibility</li>
                          <li>• Strengthens targeted muscle groups</li>
                          <li>• Enhances coordination and balance</li>
                          <li>• Supports functional movement patterns</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-2">Recommended For</h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                          <li>• Post-surgical rehabilitation</li>
                          <li>• Chronic pain management</li>
                          <li>• Mobility improvement programs</li>
                          <li>• General strength building</li>
                        </ul>
                      </div>

                      <div className="pt-4 border-t">
                        <p className="text-sm text-gray-500 text-center">
                          Select a patient from the dropdown to view their specific analytics for this game
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Overall Statistics Across All Patients */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Overall Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#6d4f79]">87%</p>
                        <p className="text-sm text-gray-600">Avg Accuracy</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#6d4f79]">145</p>
                        <p className="text-sm text-gray-600">Total Sessions</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#6d4f79]">18</p>
                        <p className="text-sm text-gray-600">Active Patients</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGameAnalytics(false)}>
              Close
            </Button>
            {selectedPatient && (
              <Button className="bg-[#6d4f79] hover:bg-[#5a3f66]">
                Export Report
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terms and Conditions Modal */}
      {showTerms && (
        <TermsAndConditions onAccept={async () => {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const userId = user.userId || user.id;
          if (userId) {
            await termsService.acceptUserTerms(userId);
            console.log('✅ Clinician terms accepted and saved to database');
          }
          setShowTerms(false);
        }} />
      )}
    </div>
  );
};

export default ClinicianDashboard;